import React, { useState, useEffect, useCallback } from 'react';
import { fetchSurveys, fetchEnrichedDeployments, fetchArchives, deleteDeployment } from '../utils/explorerApi';
import { formatNLabel } from '../utils/deploymentStats';
import { showToast } from './Toast';
import ConfirmModal from './ConfirmModal';
import { tokens, panelStyles } from './listPanelStyles';

/**
 * Build a user-meaningful label for a deployment. Users don't recognize
 * raw slugs like "R6MAVsDJUp" or "deploy-21ce9498-1771345540864"; they
 * reason about deployments by date. Falls back gracefully if fields are
 * missing. Optionally takes a (i, total) pair for batch context.
 *
 * Counts are intentionally NOT part of this label — the confirmation states
 * n = y/x separately (via formatNLabel) so a single, unambiguous count pair
 * is shown, never a second started/completed pair alongside it.
 */
function describeDeployment(dep, i, total) {
    if (!dep) return 'this deployment';
    const date = dep.created_at ? new Date(dep.created_at).toLocaleDateString() : null;
    const base = date ? `the deployment from ${date}` : 'this deployment';
    if (typeof i === 'number' && typeof total === 'number') {
        return `deployment ${i + 1} of ${total} — ${base}`;
    }
    return base;
}

/**
 * Translate a delete failure from sam-dbam into a plain, actionable message.
 * The one case the server still refuses is a live (open) deployment, which the
 * UI also gates. Surface that reason and point at the right tool rather than a
 * bare "couldn't delete".
 */
function deleteErrorMessage(err, label) {
    const code = err?.data?.error?.code || '';
    if (err?.status === 409 && code === 'DEPLOYMENT_OPEN') {
        return `Couldn't delete ${label}: it is live. Undeploy it in the console first.`;
    }
    return `Couldn't delete ${label}.`;
}

/**
 * DeploymentManagerPanel
 *
 * Renders the Deployment Manager interface. Handles cross-survey grouping,
 * filters, checkboxes, and sequential batch deletion workflows.
 *
 * Archiving lives in the Archiver (menu-bar tool), not here. The archive
 * unit is the survey, not the deployment — a deployment cannot be
 * archived in isolation. See docs/v1/v1_release_scope.md §5 Archiving.
 *
 * There is exactly ONE Delete button on this surface. It sits in the
 * selection/action row above the list and deletes the current selection. Rows
 * carry a checkbox and nothing else — no per-row Delete, and no Actions column.
 * See "UI artifacts/list-management.md" §Required structure and
 * §Destructive actions.
 *
 * Delete rules follow the lifecycle state machine (round 2):
 *   - An Open (live) deployment is NEVER deletable here. Its removal path is
 *     Undeploy in the console (which closes it). Its checkbox is disabled with
 *     a tooltip; the rule is also enforced server-side (409 DEPLOYMENT_OPEN).
 *   - A Closed deployment IS deletable, including one with results (n = y/x)
 *     and including one that was never archived, always behind an explicit
 *     confirmation stating n, permanence, and archive status. The
 *     confirmation informs; it does not argue. Once the author confirms, the
 *     delete goes through — sam-dbam no longer demands an archive first.
 */
export default function DeploymentManagerPanel({ surveyId: initialSurveyId, onClose, onRefreshDeployments }) {
    // Data states
    const [surveys, setSurveys] = useState([]);
    const [deployments, setDeployments] = useState([]);
    const [archivedIds, setArchivedIds] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [actionProgress, setActionProgress] = useState('');

    // Filter/Selection states
    const [selectedSurveyId, setSelectedSurveyId] = useState(initialSurveyId || 'all');
    const [groupBySurvey, setGroupBySurvey] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all'); // all, open, closed
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Confirmation modal — { kind: 'single'|'batch', ids: string[], busy: bool }
    // or null when closed. Native window.confirm is banned project-wide.
    const [confirmState, setConfirmState] = useState(null);

    // Survey lookup map: { [id]: name }
    const [surveyMap, setSurveyMap] = useState({});

    // Load initial data
    useEffect(() => {
        const loadInitial = async () => {
            setLoading(true);
            setError('');
            try {
                const surveyList = await fetchSurveys();
                setSurveys(surveyList || []);
                const map = {};
                surveyList.forEach(s => { map[s.id] = s.name; });
                setSurveyMap(map);
            } catch (err) {
                setError(err.message || 'Failed to load surveys');
            } finally {
                setLoading(false);
            }
        };
        loadInitial();
    }, []);

    // Load deployments (and archive membership) based on filter. Archive
    // membership drives the "archived / not archived" line in the delete
    // confirmation — no extra server column needed; survey_archives already
    // carries deployment_id.
    const loadDeployments = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            let list = [];
            if (selectedSurveyId === 'all') {
                // Fetch deployments across all surveys
                // We fetch the surveys list first, then call enriched deployments for each survey
                const promises = surveys.map(s => fetchEnrichedDeployments(s.id).catch(() => []));
                const results = await Promise.all(promises);
                results.forEach((res, idx) => {
                    const deps = (res || []).map(d => ({
                        ...d,
                        survey_name: surveys[idx].name,
                        survey_id: surveys[idx].id
                    }));
                    list = [...list, ...deps];
                });
            } else {
                const res = await fetchEnrichedDeployments(selectedSurveyId);
                list = (res || []).map(d => ({
                    ...d,
                    survey_name: surveyMap[selectedSurveyId] || 'Untitled Survey',
                    survey_id: selectedSurveyId
                }));
            }
            setDeployments(list);

            // Archive membership (best-effort — a failure here just means the
            // confirmation can't assert an archive exists, which is safe).
            try {
                const archives = await fetchArchives(selectedSurveyId === 'all' ? undefined : selectedSurveyId);
                const ids = new Set((archives || []).map(a => a.deployment_id).filter(Boolean));
                setArchivedIds(ids);
            } catch (_) {
                setArchivedIds(new Set());
            }

            // Clear selection
            setSelectedIds(new Set());
        } catch (err) {
            setError(err.message || 'Failed to load deployments');
        } finally {
            setLoading(false);
        }
    }, [selectedSurveyId, surveys, surveyMap]);

    useEffect(() => {
        if (surveys.length > 0) {
            loadDeployments();
        }
    }, [selectedSurveyId, surveys, loadDeployments]);

    // Only closed deployments are selectable for deletion — open (live) ones
    // are removed by undeploying in the console, never here.
    const toggleSelect = (d) => {
        if (d.is_open !== false) return; // open rows are not selectable
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(d.deployment_id)) next.delete(d.deployment_id); else next.add(d.deployment_id);
            return next;
        });
    };

    // Filter deployments
    const getFilteredDeployments = () => {
        return deployments.filter(d => {
            const matchesSearch = d.deploy_slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (d.survey_name || '').toLowerCase().includes(searchQuery.toLowerCase());
            const matchesStatus = statusFilter === 'all' ||
                (statusFilter === 'open' && d.is_open) ||
                (statusFilter === 'closed' && !d.is_open);
            return matchesSearch && matchesStatus;
        });
    };

    const filtered = getFilteredDeployments();
    // Closed rows are the only deletable set — drives Select-all and batch.
    const filteredClosed = filtered.filter(d => d.is_open === false);
    const allClosedSelected = filteredClosed.length > 0 &&
        filteredClosed.every(d => selectedIds.has(d.deployment_id));

    const toggleSelectAll = () => {
        if (allClosedSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredClosed.map(d => d.deployment_id)));
        }
    };

    // Open the confirmation for the current (closed-only) selection. This is
    // the only delete entry point in the panel. A selection of exactly one gets
    // the fuller single-deployment wording; anything more gets the list form.
    const requestBatchDelete = () => {
        if (selectedIds.size === 0) return;
        const ids = Array.from(selectedIds);
        setConfirmState({ kind: ids.length === 1 ? 'single' : 'batch', ids, busy: false });
    };

    // Run the deletion(s) after confirmation. Sequential so per-deployment
    // failures (e.g. DEPLOYMENT_OPEN) are reported precisely.
    const runDelete = async () => {
        if (!confirmState) return;
        const idsToDelete = confirmState.ids;
        setConfirmState(cs => (cs ? { ...cs, busy: true } : cs));
        setLoading(true);

        let deletedCount = 0;
        const failures = [];
        for (let i = 0; i < idsToDelete.length; i++) {
            const depId = idsToDelete[i];
            const dep = deployments.find(d => d.deployment_id === depId);
            const label = describeDeployment(dep);
            setActionProgress(`Deleting ${describeDeployment(dep, i, idsToDelete.length)}…`);
            try {
                await deleteDeployment(depId);
                deletedCount++;
            } catch (err) {
                console.error(`Delete failed for ${depId}:`, err);
                failures.push(deleteErrorMessage(err, label));
            }
        }

        setConfirmState(null);
        setSelectedIds(new Set());
        setActionProgress('');
        setLoading(false);
        await loadDeployments();
        onRefreshDeployments?.();

        if (failures.length === 0) {
            showToast(`Deleted ${deletedCount} deployment${deletedCount === 1 ? '' : 's'}.`, 'success');
        } else if (deletedCount === 0) {
            // Single most-relevant reason (all one deployment, or a shared cause).
            showToast(failures[0], 'error');
        } else {
            showToast(
                `Deleted ${deletedCount} of ${idsToDelete.length}. ${failures.length} couldn't be removed: ${failures[0]}`,
                'error'
            );
        }
    };

    // ----- Confirmation modal content -----
    const isArchived = (depId) => archivedIds.has(depId);

    const renderConfirmMessage = () => {
        if (!confirmState) return null;
        if (confirmState.kind === 'single') {
            const d = deployments.find(x => x.deployment_id === confirmState.ids[0]);
            if (!d) return null;
            return (
                <div>
                    <p style={styles.confirmLine}>
                        Permanently delete {describeDeployment(d)}?
                    </p>
                    <p style={styles.confirmLine}>
                        <strong>n = {formatNLabel(d)}.</strong> Its sessions and responses
                        will be permanently removed. This cannot be undone.
                    </p>
                    <p style={styles.confirmLine}>
                        {isArchived(d.deployment_id)
                            ? 'This deployment has been archived — a copy is preserved.'
                            : 'This deployment has not been archived, so no copy of its data will remain.'}
                    </p>
                </div>
            );
        }
        // batch
        const rows = confirmState.ids
            .map(id => deployments.find(x => x.deployment_id === id))
            .filter(Boolean);
        return (
            <div>
                <p style={styles.confirmLine}>
                    Permanently delete these {rows.length} closed deployment{rows.length === 1 ? '' : 's'}?
                    Sessions and responses will be permanently removed. This cannot be undone.
                </p>
                <ul style={styles.confirmList}>
                    {rows.map(d => (
                        <li key={d.deployment_id} style={styles.confirmListItem}>
                            {d.survey_name ? `${d.survey_name} — ` : ''}
                            {d.created_at ? new Date(d.created_at).toLocaleDateString() : d.deploy_slug}
                            {' · '}n = {formatNLabel(d)}
                            {' · '}{isArchived(d.deployment_id) ? 'archived' : 'not archived'}
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    // Grouping logic
    const renderTableRows = () => {
        if (selectedSurveyId === 'all' && groupBySurvey) {
            // Group deployments by survey_name
            const grouped = {};
            filtered.forEach(d => {
                const name = d.survey_name || 'Untitled Survey';
                if (!grouped[name]) grouped[name] = [];
                grouped[name].push(d);
            });

            return Object.keys(grouped).map(surveyName => (
                <React.Fragment key={surveyName}>
                    <tr style={styles.groupHeaderRow}>
                        <td colSpan="5" style={styles.groupHeaderCell}>
                            {surveyName}
                        </td>
                    </tr>
                    {grouped[surveyName].map(d => renderRow(d))}
                </React.Fragment>
            ));
        }

        return filtered.map(d => renderRow(d));
    };

    const renderRow = (d) => {
        const isSelected = selectedIds.has(d.deployment_id);
        const isOpen = d.is_open !== false; // live deployment — not deletable here

        return (
            <tr key={d.deployment_id} style={isSelected ? styles.rowSelected : styles.row}>
                <td style={styles.cell}>
                    <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isOpen}
                        title={isOpen ? 'Live deployment — undeploy in the console first.' : 'Select for deletion'}
                        onChange={() => toggleSelect(d)}
                    />
                </td>
                <td style={styles.cell}>
                    <div>
                        <div style={styles.slugVal}>{d.deploy_slug}</div>
                        {selectedSurveyId === 'all' && !groupBySurvey && (
                            <div style={styles.surveySubText}>{d.survey_name}</div>
                        )}
                    </div>
                </td>
                <td style={styles.cell}>
                    <span style={{
                        ...styles.statusBadge,
                        ...(d.is_open ? styles.statusBadgeOpen : styles.statusBadgeClosed)
                    }}>
                        {d.is_open ? 'Open' : 'Closed'}
                    </span>
                </td>
                <td style={styles.cell}>
                    {new Date(d.created_at).toLocaleDateString()}
                </td>
                <td style={styles.cell}>
                    <div style={styles.sessionsVal}>
                        {d.started_count || 0} started / {d.completed_count || 0} completed
                    </div>
                </td>
            </tr>
        );
    };

    return (
        <div style={styles.container}>
            {/* Filter controls */}
            <div style={styles.filterBar}>
                <div style={styles.filterGroup}>
                    <label style={styles.filterLabel}>Survey:</label>
                    <select
                        style={styles.select}
                        value={selectedSurveyId}
                        onChange={(e) => setSelectedSurveyId(e.target.value)}
                    >
                        <option value="all">All Surveys</option>
                        {surveys.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>

                {selectedSurveyId === 'all' && (
                    <div style={styles.checkboxGroup}>
                        <input
                            type="checkbox"
                            id="group-by-survey"
                            checked={groupBySurvey}
                            onChange={(e) => setGroupBySurvey(e.target.checked)}
                        />
                        <label htmlFor="group-by-survey" style={styles.checkboxLabel}>Group by Survey</label>
                    </div>
                )}

                <div style={styles.filterGroup}>
                    <label style={styles.filterLabel}>Status:</label>
                    <select
                        style={styles.select}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All Statuses</option>
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                    </select>
                </div>

                <div style={styles.searchGroup}>
                    <input
                        type="text"
                        style={styles.searchInput}
                        placeholder="Search deployments..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {/* Progress / Loading alerts */}
            {actionProgress && <div style={styles.progressAlert}>{actionProgress}</div>}
            {error && <div style={styles.errorAlert}>{error}</div>}

            {/* Selection/action row. This is the ONLY place a deployment can be
                deleted — one Delete button acting on the current selection. It
                stays visible so the count and the action are always in the same
                spot, and Delete is simply disabled when nothing is selected. */}
            <div style={styles.batchBar}>
                <span style={styles.batchCount}>
                    {selectedIds.size === 0
                        ? 'No deployments selected'
                        : `${selectedIds.size} closed deployment${selectedIds.size === 1 ? '' : 's'} selected`}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                    {selectedIds.size > 0 && (
                        <button
                            style={styles.batchBtn}
                            title="Clear the current selection"
                            onClick={() => setSelectedIds(new Set())}
                        >
                            Clear
                        </button>
                    )}
                    <button
                        style={{
                            ...styles.batchBtnDelete,
                            ...(selectedIds.size === 0 ? styles.batchBtnDisabled : {}),
                        }}
                        disabled={selectedIds.size === 0}
                        title={selectedIds.size === 0
                            ? 'Select one or more closed deployments first.'
                            : 'Delete the selected closed deployments'}
                        onClick={requestBatchDelete}
                    >
                        Delete Selected
                    </button>
                </div>
            </div>

            {/* Table */}
            <div style={styles.tableWrapper}>
                {loading && !actionProgress && <div style={styles.loading}>Loading deployments...</div>}
                {!loading && filtered.length === 0 && (
                    <div style={styles.empty}>No deployments found matching the criteria.</div>
                )}
                {filtered.length > 0 && (
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.headerRow}>
                                <th style={{ ...styles.headerCell, width: '40px' }}>
                                    <input
                                        type="checkbox"
                                        checked={allClosedSelected}
                                        disabled={filteredClosed.length === 0}
                                        title={filteredClosed.length === 0 ? 'No closed deployments to select' : 'Select all closed deployments'}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th style={styles.headerCell}>Deploy Slug</th>
                                <th style={styles.headerCell}>Status</th>
                                <th style={styles.headerCell}>Created</th>
                                <th style={styles.headerCell}>SRA Activity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {renderTableRows()}
                        </tbody>
                    </table>
                )}
            </div>

            <ConfirmModal
                open={!!confirmState}
                title={confirmState?.kind === 'batch' ? 'Delete deployments' : 'Delete deployment'}
                message={renderConfirmMessage()}
                confirmLabel={confirmState?.kind === 'batch'
                    ? `Delete ${confirmState.ids.length}`
                    : 'Delete'}
                danger
                busy={!!confirmState?.busy}
                onConfirm={runDelete}
                onCancel={() => setConfirmState(null)}
            />
        </div>
    );
}

// Local style names are kept for JSX legibility, but values are sourced
// from listPanelStyles (the project's shared Tools-panel tokens, modeled on
// Console's RecipientBrowser). Panel-specific extras (status badges, group
// header rows, slug typography) compose on top of the shared base.
const styles = {
    container: panelStyles.shell,
    filterBar: panelStyles.filterBar,
    filterGroup: {
        display: 'flex', alignItems: 'center', gap: 6,
    },
    filterLabel: panelStyles.fieldLabel,
    select: panelStyles.select,
    checkboxGroup: {
        display: 'flex', alignItems: 'center', gap: 6,
    },
    checkboxLabel: panelStyles.checkboxLabel,
    searchGroup: {
        marginLeft: 'auto',
    },
    searchInput: { ...panelStyles.searchInput, width: 220, flex: 'none' },

    progressAlert: {
        ...panelStyles.loadingBar,
        background: tokens.primaryFaint,
        color: tokens.primary,
        fontWeight: tokens.fontWeightMedium,
        borderBottom: `1px solid ${tokens.borderLight}`,
    },
    errorAlert: panelStyles.errorBar,

    batchBar: { ...panelStyles.batchBar, justifyContent: 'space-between' },
    batchCount: panelStyles.batchBarLabel,
    batchBtn: panelStyles.batchBarBtn,
    batchBtnDelete: panelStyles.batchBarBtnDanger,
    batchBtnDisabled: panelStyles.batchBarBtnDisabled,

    tableWrapper: { ...panelStyles.tableContainer, padding: 0 },
    table: panelStyles.table,
    headerRow: {
        background: tokens.panelBg,
    },
    headerCell: { ...panelStyles.th, padding: '10px 16px' },

    // Group-by-Survey header row sits above each survey's deployments.
    groupHeaderRow: {
        background: tokens.borderRowDivider,
    },
    groupHeaderCell: {
        padding: '8px 16px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.textPrimary,
        fontSize: tokens.fontSizeSm,
    },

    row: panelStyles.row,
    rowSelected: panelStyles.rowSelected,
    cell: { ...panelStyles.td, padding: '10px 16px' },

    slugVal: {
        fontWeight: tokens.fontWeightMedium,
        color: tokens.primary,
    },
    surveySubText: {
        fontSize: tokens.fontSizeXs,
        color: tokens.textTertiary,
        marginTop: 2,
    },

    // Status pill — open vs closed deployment.
    statusBadge: {
        fontSize: tokens.fontSizeXs,
        fontWeight: tokens.fontWeightMedium,
        padding: '2px 8px',
        borderRadius: tokens.radiusPill,
        display: 'inline-block',
    },
    statusBadgeOpen: {
        background: tokens.successBg,
        color: '#137333',
    },
    statusBadgeClosed: {
        background: tokens.borderRowDivider,
        color: tokens.textSecondary,
    },

    sessionsVal: {
        color: tokens.textPrimary,
    },

    // Confirmation modal body.
    confirmLine: {
        margin: '0 0 10px',
    },
    confirmList: {
        margin: '8px 0 0',
        paddingLeft: 18,
        maxHeight: 180,
        overflowY: 'auto',
    },
    confirmListItem: {
        marginBottom: 4,
        fontSize: tokens.fontSizeSm,
    },

    loading: {
        padding: tokens.space2xl,
        textAlign: 'center',
        color: tokens.textSecondary,
    },
    empty: panelStyles.emptyState,
};
